<script setup lang="ts">
import {
  useAwardsStore,
  useHotelStore,
  useLabelsStore,
  usePagesStore,
  useSocialMediaStore,
  useTermsStore,
} from 'pinia-store'
import { isValidUrl } from '@ui/helpers/functions'
import { useRoute } from 'vue-router'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination } from 'swiper/modules'
import { toQueryUrl } from '@ui/composables'
import { stripHtml } from '@general-function'
import LinkSocialMedia from '@ui/base-components/LinkSocialMedia.vue'
import { initMenu } from '../../../composables'
import { setSpaceToDash } from '../../../helpers/functions'
import { useI18n, useLocalePath } from '#imports'

const { locale: language } = useI18n()
const route = useRoute()
const localePath = useLocalePath()

const useSocialMedia = useSocialMediaStore()
const useHotel = useHotelStore()
const useTerms = useTermsStore()
const useLabels = useLabelsStore()
const useAwards = useAwardsStore()
const usePages = usePagesStore()

const profile = useHotel.getProfile
const labels = useLabels.getLabels

const awards = computed(() => useAwards.getList)
const menuGeneral = computed(() => useTerms.getGeneralTerms)
const menuTerms = computed(() => useTerms.getListTerms.filter((el: any) => el?.slug !== 'general-terms'))
const menuGeneralTerms = computed(() => useTerms.getListTerms.filter((el: any) => el?.slug === 'general-terms'))
const menu = computed(() => initMenu(usePages.getMenuFooter, 'is_footer'))
const socmed = computed(() => useSocialMedia.getList)
const isAtNotFoundPage = computed(() => route.name === 'not-found')
const progressWrap = ref<Element | null>(null)
const isMounted = ref<boolean>(false)
const offset = ref<number>(50)

function handleScroll() {
  const el = document.querySelector('.progress-wrap')
  if (window.scrollY > offset.value && el)
    el?.classList.add('active-progress')
  else if (el)
    el?.classList.remove('active-progress')
}

function createMenuLink(obj) {
  let req = obj
  if (isAtNotFoundPage) {
    req = {
      ...req,
      params: {
        ...req?.params,
        language: route.params?.language || language.value,
      },
    }
  }
  else {
    req = localePath({ ...req })
  }
  return req
}

function init() {
  const nuxt = document.getElementById('__nuxt')
  if (nuxt) {
    nuxt.style.backgroundColor = '#ffff'
    nuxt.style.float = 'left'
    nuxt.style.width = '100%'
    nuxt.style.overflow = 'hidden'
    nuxt.style.position = 'relative'
  }

  // Scroll back to top
  const progressPath: any = document.querySelector('.progress-wrap path') as HTMLInputElement
  const pathLength = progressPath.getTotalLength()
  progressPath.style.transition = progressPath.style.WebkitTransition = 'none'
  progressPath.style.strokeDasharray = `${pathLength} ${pathLength}`
  progressPath.style.strokeDashoffset = pathLength
  progressPath.getBoundingClientRect()
  progressPath.style.transition = progressPath.style.WebkitTransition = 'stroke-dashoffset 10ms linear'

  const updateProgress = function () {
    const scroll = window.scrollY
    const height = document.documentElement.scrollHeight - window.innerHeight
    const progress = pathLength - (scroll * pathLength) / height
    progressPath.style.strokeDashoffset = progress
  }
  updateProgress()
  if (typeof window !== 'undefined')
    window.addEventListener('scroll', updateProgress) // TODO: destroy when unmounted
}

onMounted(() => {
  init()
  if (window && typeof window !== 'undefined')
    window.addEventListener('scroll', handleScroll)

  progressWrap.value = document.querySelector('.progress-wrap')
  progressWrap.value?.addEventListener('click', handleProgressWrapClick)
})

function handleProgressWrapClick() {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth',
  })
}
onMounted(() => {
  isMounted.value = true
})
onUnmounted(() => {
  if (window && typeof window !== 'undefined') {
    window.removeEventListener('scroll', handleScroll)
    progressWrap.value?.removeEventListener('click', handleProgressWrapClick)
  }
})
</script>

<template>
  <footer>
    <div class="fancy-border"></div>
    <ClientOnly
      fallback-tag="span"
      fallback="Loading..."
    >
      <div v-if="isMounted" class="already-visible pb-5 pt-10 container">
        <div class="footer-grid grid py-5">
          <NuxtLink
            :to="localePath({
              name: 'home',
            })"
            class="footer-logo mb-md-3 mb-5"
          >
            <img
              v-if="profile && profile.hotel_logo_color"
              :src="profile.hotel_logo_color"
              class="footer-logo"
              :alt="profile.hotel_name"
            >
          </NuxtLink>
          <div
            class="mb-md-3 footer-social-media mb-4"
          >
            <div
              v-if="socmed && Array.isArray(socmed) && socmed.length"
              id="section-contact-us"
              class="mb-8 flex flex-wrap justify-center gap-4 lg:!justify-start"
            >
              <LinkSocialMedia
                v-for="(el, key) in socmed"
                id="link"
                :key="key"
                class="flex justify-center hover:underline"
                :size="48"
                :model-value="el"
                :disabled="false"
                color="var(--color-primary)"
                :is-primary="false"
                :is-outline="false"
              />
            </div>
            <div
              v-if="labels.CONTACT_PAGE_DESCRIPTION"
              class="mt-4"
              v-html="stripHtml(labels.CONTACT_PAGE_DESCRIPTION)"
            ></div>
            <template v-else>
              <small class="mt-4 text-sm">
                {{ profile.hotel_address }}
              </small>

              <ul class="mt-4 text-sm">
                <li v-if="profile.hotel_email">
                  <a
                    v-for="(el, key) in profile.hotel_email.split(',')"
                    :key="key"
                    :title="el"
                    :aria-label="el"
                    class="font-bold"
                    :href="`mailto:${el}`"
                    rel="nooper"
                    :class="`${profile.hotel_email.split(',').length > 1 ? 'block' : ''}`"
                    v-text="el"
                  ></a>
                </li>
                <li v-if="profile.hotel_phone">
                  <a
                    class="font-bold"
                    :title="profile.hotel_phone"
                    :aria-label="profile.hotel_phone"
                    :href="`https://wa.me/${profile.hotel_phone}`"
                    rel="nooper"
                    v-text="profile.hotel_phone"
                  ></a>
                </li>
              </ul>
            </template>
          </div>

          <div class="footer-explore mb-sm-2">
            <template v-if="menu && Array.isArray(menu) && menu.length" >
              <h6 class="mb-4">
                Explore
              </h6>
              <ul class="grid grid-cols-2 mt-4 gap-x-8 gap-y-2">
                <li v-for="item in menu" :key="setSpaceToDash(item.slug)">
                  <a
                    v-if="item.redirect_url"
                    class="hover:underline"
                    :title="item.title"
                    :href="item.redirect_url"
                    rel="nofollow noopener noreferrer"
                  >
                    {{ stripHtml(item.title, false) }}
                  </a>
                  <NuxtLink
                    v-else-if="item?.slug"
                    :to="localePath({
                      name: item.slug,
                      params: {
                        slug: item?.child,
                      },
                    })
                    "
                    :title="item.title"
                    class="hover:underline"
                  >
                    {{ stripHtml(item.title, false) }}
                  </NuxtLink>
                </li>
              </ul>
            </template>
          </div>
          <div
            v-if="useAwards?.list && Array.isArray(useAwards?.list) && useAwards?.list.length"
            class="footer-certificate"
          >
            <ClientOnly
              fallback-tag="span"
              fallback="Loading..."
            >
              <Swiper
                :breakpoints="{
                  320: {
                    slidesPerView: 2,
                  },
                  576: {
                    slidesPerView: 2,
                  },
                  768: {
                    slidesPerView: 2,
                  },
                  992: {
                    slidesPerView: 3,
                  },
                  1200: {
                    slidesPerView: 3,
                  },
                  1400: {
                    slidesPerView: 3,
                  },
                }"
                :space-between="10"
                :grab-cursor="true"
                :auto-height="true"
                :pagination="{
                  el: '.swiper-awards-pagination',
                  clickable: true,
                  enabled: awards.length > 2,
                }"
                :modules="[Pagination]"
                class="grid grid-cols-3 gap-1"
              >
                <SwiperSlide
                  v-for="(el, k) in awards"
                  :key="k"
                >
                  <a
                    class="flex hover:underline"
                    rel="noopener"
                    :title="el.award_title"
                    :aria-label="el.award_title"
                    target="_blank"
                    :href="el?.award_custom_url || '/'"
                  >
                    <VueImg
                      :src="toQueryUrl(el.award_image, {
                        d: '70x70',
                      })" :alt="el.award_title" :width="70" height="auto"
                    />
                  </a>
                </SwiperSlide>
              </Swiper>
            </ClientOnly>
            <div class="swiper-awards-pagination mt-4 h-10"></div>
          </div>
        </div>
      </div>
      <div class="footer-bottom border-t-2 py-2">
        <div class="container">
          <div class="grid grid-cols-1 lg:grid-cols-2">
            <div class="footer-copyright col-sm-12 col-md-12 col-xs-6 col-lg-8 order-2 my-1 lg:!order-1">
              <small class="text-sm">Copyright &copy; {{ new Date().getFullYear() }},
                <button
                  type="button"
                  title="Archipelago"
                  class="transparent px-0 shadow-none hover:underline"
                  @click="handleClickLink('https://www.archipelagohotels.com/powered-by-archipelago')"
                >Powered by Archipelago</button>.
              </small>
            </div>
            <div class="col-xs-6 col-md-12 col-sm-12 footer-privacy col-lg-4 order-1 my-1 lg:!order-2">
              <ul
                v-if="(menuTerms && menuTerms.length) || (menuGeneral && menuGeneral.length)"
                class="flex items-center gap-4 text-sm"
              >
                <li
                  v-if="menuGeneralTerms && menuGeneralTerms.length"
                  class="font-secondary text-nowrap"
                >
                  <NuxtLink
                    class="text-black hover:underline"
                    :title="menuGeneralTerms[0].title"
                    :to="createMenuLink({
                      name: menuGeneralTerms[0].slug,
                    })"
                  >
                    {{ stripHtml(menuGeneralTerms[0].title, false) }}
                  </NuxtLink>
                </li>
                <li
                  v-else-if="menuGeneral && menuGeneral.length"
                  class="font-secondary text-nowrap"
                >
                  <NuxtLink
                    class="text-black hover:underline"
                    title="General Terms"
                    :to="createMenuLink({
                      name: 'general-terms',
                    })"
                    v-html="'General Terms'"
                  >
                  </NuxtLink>
                </li>
                <li
                  v-for="(el, index) in menuTerms"
                  :key="index"
                  class="font-secondary text-nowrap"
                >
                  <template v-if="el && el?.slug">
                    <template v-if="el?.title === 'dots'">
                      <ICircleSmall class="p-0" size="16"/>
                    </template>
                    <a
                      v-else-if="el?.title && isValidUrl(el?.slug)"
                      class="text-black hover:underline"
                      target="_blank"
                      rel="noopener"
                      aria-label="link"
                      :title="el.title"
                      :href="el.slug"
                    >
                      {{ stripHtml(el.title, false) }}
                    </a>
                    <NuxtLink
                      v-else-if="el?.title"
                      class="text-black hover:underline"
                      :title="el.title"
                      :to="createMenuLink({
                        name: 'general-terms-slug',
                        params: {
                          slug: el.slug,
                        },
                      })"
                    >
                      {{ stripHtml(el.title, false) }}
                    </NuxtLink>
                  </template>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ClientOnly>
  </footer>
  <div class="progress-wrap">
    <svg class="progress-circle" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16 0.5C20.1109 0.5 24.0533 2.13303 26.9602 5.03984C29.867 7.94666 31.5 11.8891 31.5 16C31.5 20.1109 29.867 24.0533 26.9602 26.9602C24.0533 29.867 20.1109 31.5 16 31.5C11.8891 31.5 7.94666 29.867 5.03984 26.9602C2.13303 24.0533 0.5 20.1109 0.5 16C0.5 11.8891 2.13303 7.94666 5.03984 5.03984C7.94666 2.13303 11.8891 0.5 16 0.5Z"
        stroke="black"
      />
      <path
        d="M16 11L16 20.75M16 11L19 14M16 11L13 14"
        stroke="black"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </div>
</template>

<style lang="scss" scoped>
@import './footer/component.scss';

:deep(.swiper-pagination-bullets) {
  height: 2rem;
  padding: 0.5rem 0;
  justify-content: center;

  .swiper-pagination-bullet {
    height: 0.75rem;
    width: 0.75rem;
    // only for facility section home, should be !important
    border-width: 0.125rem;
    border-style: solid;
    border-color: var(--color-secondary) !important;
    background-color: transparent;
    @include box-shadow(0 0px 2px white);

    &.swiper-pagination-bullet-active {
      background-color: var(--color-secondary);
      @include box-shadow(0 0px 2px white);
      opacity: 1;
    }

    opacity: 0.5;
  }
}
</style>
